import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get as _get } from '@ember/object';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import type { ContentValue } from '@glint/template';
import type { TableRowMeta } from 'ember-table';
import type EmberTrComponent from 'ember-table/components/ember-tr/component';
import { and, eq } from 'ember-truth-helpers';

import styleEntry from '../../../helpers/style-entry.ts';
import type { IconName } from '../../../types/components/prism/icon.ts';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '../../../types/components/prism/table.ts';
import PrismButton from '../button.gts';
import PrismCheckbox from '../checkbox.gts';
import PrismIcon from '../icon.gts';
import type { PrismTableSignature } from '../table.gts';
import PrismTooltip from '../tooltip.gts';

const debounce = <This, Args extends unknown[], R>(
  fn: (this: This, ...args: Args) => R,
  ms = 300,
): ((this: This, ...args: Args) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: This, ...args: Args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

interface PrismTableRowSignature {
  Args: {
    body: {
      row: typeof EmberTrComponent<CustomEmberTableRow, CustomEmberTableColumn>;
      rowMeta: TableRowMeta;
      rowValue: CustomEmberTableRow;
    };
    checkboxSelectionMode: PrismTableSignature['Args']['checkboxSelectionMode'];
    disableCheckboxSelection?: boolean;
    disabledCheckboxIcon?: IconName;
    disabledCheckboxTooltipText?: string;
    isFooterRow?: boolean;
    onRowClick?: PrismTableSignature['Args']['onRowClick'];
    onRowCollapseToggle?: PrismTableSignature['Args']['onRowCollapseToggle'];
    onRowHover?: PrismTableSignature['Args']['onRowHover'];
  };
}

export default class PrismTableRow extends Component<PrismTableRowSignature> {
  debouncedMouseEnter = debounce((event: Event): void => {
    const { body } = this.args;

    this.args.onRowHover?.({
      event,
      rowValue: body.rowValue,
      rowMeta: body.rowMeta,
    });
  }, 50);

  debouncedMouseLeave = debounce((event: Event): void => {
    this.args.onRowHover?.({ event, rowValue: null, rowMeta: null });
  }, 50);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledCheckboxIcon() {
    const { body, disabledCheckboxIcon } = this.args;

    return body.rowValue.disabledCheckboxIcon ?? disabledCheckboxIcon;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disabledCheckboxTooltipText() {
    const { body, disabledCheckboxTooltipText } = this.args;

    return (
      body.rowValue.disabledCheckboxTooltipText ?? disabledCheckboxTooltipText
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCheckboxDisabled() {
    const { body, disableCheckboxSelection } = this.args;

    return body.rowValue.disableCheckboxSelection ?? disableCheckboxSelection;
  }

  getCellValue(
    row: CustomEmberTableRow,
    column: CustomEmberTableColumn,
  ): ContentValue {
    if (column.displayValuePath) {
      return _get(row, column.displayValuePath) as ContentValue;
    }

    if (column.valuePath) {
      return _get(row, column.valuePath) as ContentValue;
    }

    // Ember Table sends 'ghost' column without a valuePath. To be investigated.
    return '';
  }

  @action
  onRowCollapseToggle(
    onCollapseToggled: () => void,
    row: CustomEmberTableRow,
  ): void {
    onCollapseToggled();
    this.args.onRowCollapseToggle?.(row);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <@body.row
      @onClick={{@onRowClick}}
      class="table-row
        {{concat 'table-row--level-' @body.rowMeta.depth}}
        {{if @isFooterRow 'table-row--footer'}}
        {{get @body.rowValue 'className'}}
        {{if @body.rowValue.isActive 'table-row--active'}}
        "
      data-test-id="table-row"
      {{on "mouseenter" this.debouncedMouseEnter}}
      {{on "mouseleave" this.debouncedMouseLeave}}
      as |bodyRow|
    >
      <bodyRow.cell
        class="table-cell
          {{if bodyRow.columnValue.hasRightBorder 'right-border'}}
          {{if
            bodyRow.columnValue.tdClassNames
            bodyRow.columnValue.tdClassNames
          }}
          "
        data-test-id="table-cell"
      >
        <:select as |rowMeta onSelectToggled|>
          {{#unless (eq @checkboxSelectionMode "none")}}
            <div class="pr-200">
              {{! Wrap in a <div> so the tooltip isn't rendered on the parent div, because it has padding. }}
              <div>
                {{#if (and this.isCheckboxDisabled this.disabledCheckboxIcon)}}
                  <PrismIcon
                    {{! @glint-expect-error: Incorrect type }}
                    @name={{this.disabledCheckboxIcon}}
                    @size="small"
                    data-test-id="row-checkbox-icon-select"
                  />
                {{else}}
                  <PrismCheckbox
                    @checked={{rowMeta.isGroupSelected}}
                    @disabled={{this.isCheckboxDisabled}}
                    @indeterminate={{rowMeta.isGroupIndeterminate}}
                    @onChange={{onSelectToggled}}
                    aria-label="Select row"
                    data-test-id="row-checkbox-select"
                  />
                {{/if}}

                {{#if
                  (and this.isCheckboxDisabled this.disabledCheckboxTooltipText)
                }}
                  <PrismTooltip
                    {{! @glint-expect-error: Incorrect type }}
                    @text={{this.disabledCheckboxTooltipText}}
                  />
                {{/if}}
              </div>
            </div>
          {{/unless}}
        </:select>
        <:collapse as |rowMeta onCollapseToggled|>
          <div
            class="table-row__collapse-button"
            style={{styleEntry "--level" (concat "" rowMeta.depth)}}
          >
            <PrismButton
              @buttonType="ghost"
              @icon={{if rowMeta.isCollapsed "chevron-right" "chevron-down"}}
              @isSmall={{true}}
              @onClick={{fn
                this.onRowCollapseToggle
                onCollapseToggled
                @body.rowValue
              }}
              aria-label={{if rowMeta.isCollapsed "Expand row" "Collapse row"}}
              data-test-id="table-row-collapse-button"
            />
          </div>
        </:collapse>
        <:default as |cellData column row cellMeta columnMeta rowMeta|>
          {{#if column.tdComponent}}
            {{#let
              (component (ensureSafeComponent column.tdComponent this))
              as |TdComponent|
            }}
              <TdComponent
                @cellData={{this.getCellValue row column}}
                @cellMeta={{cellMeta}}
                @column={{column}}
                @columnMeta={{columnMeta}}
                @displayValue={{this.getCellValue row column}}
                @row={{row}}
                @rowMeta={{rowMeta}}
                @value={{get row column.valuePath}}
              />
            {{/let}}
          {{else}}
            <div class={{if column.hasLineClampTwo "line-clamp-2" "truncate"}}>
              {{this.getCellValue row column}}
            </div>
          {{/if}}
        </:default>
      </bodyRow.cell>
    </@body.row>
  </template>
}
